import notesMenu from '../config/notesMenu'
import purchasesMenu from '../config/purchasesMenu'
import settingsMenu from '../config/settingsMenu'
import utilityBillsMenu from '../config/utilityBillsMenu'
import { AuthUser } from '../domains/Login/types/users'
import {
  ModulePermission,
  normalizeUserModules,
} from '../domains/Settings/config/userModules'
import { MenuItem } from '../types'

const noteLandingRoutes = notesMenu.map((item) => ({
  code: item.code || item.path,
  path: item.path,
}))

const topLevelLandingRoutes = [
  { code: 'objectives', path: '/objectives' },
  { code: 'events', path: '/events' },
  { code: 'translation', path: '/translation' },
  { code: 'whiteboards', path: '/whiteboards' },
  { code: 'files', path: '/files' },
  { code: 'scenarios', path: '/scenarios' },
  { code: 'purchases', path: '/purchases?tab=daily' },
  { code: 'settings', path: '/settings/users' },
]

const moduleLandingRoutes = [...noteLandingRoutes, ...topLevelLandingRoutes]

export const getModulePermission = (
  user: AuthUser | undefined,
  code: string,
): ModulePermission => {
  if (!user) {
    return 'hide'
  }

  if (code === 'protocols' && !user.isProtocolsActive) {
    return 'hide'
  }

  if (user.role === 'admin') {
    return 'edit'
  }

  const matchedModule = normalizeUserModules(user.modules).find(
    (moduleItem) => moduleItem.code === code,
  )

  return matchedModule?.permission || 'hide'
}

export const canViewModule = (
  user: AuthUser | undefined,
  code: string,
): boolean => {
  const permission = getModulePermission(user, code)
  return permission === 'view' || permission === 'edit'
}

export const canEditModule = (
  user: AuthUser | undefined,
  code: string,
): boolean => {
  return getModulePermission(user, code) === 'edit'
}

export const getVisibleNoteMenuItems = (
  user: AuthUser | undefined,
): MenuItem[] => {
  return notesMenu.filter((item) => canViewModule(user, item.code || item.path))
}

export const getDefaultNotesRoute = (
  user: AuthUser | undefined,
): string | undefined => {
  return getVisibleNoteMenuItems(user)[0]?.path
}

export const getVisiblePurchaseMenuItems = (
  user: AuthUser | undefined,
): MenuItem[] => {
  if (!canViewModule(user, 'purchases')) {
    return []
  }

  return purchasesMenu
}

export const getDefaultPurchasesRoute = (
  user: AuthUser | undefined,
): string | undefined => {
  return getVisiblePurchaseMenuItems(user)[0]?.path
}

export const getVisibleUtilityBillsMenuItems = (
  user: AuthUser | undefined,
): MenuItem[] => {
  if (!canViewModule(user, 'purchases')) {
    return []
  }

  return utilityBillsMenu
}

export const getDefaultUtilityBillsRoute = (
  user: AuthUser | undefined,
): string | undefined => {
  return getVisibleUtilityBillsMenuItems(user)[0]?.path
}

export const getVisibleSettingsMenuItems = (
  user: AuthUser | undefined,
): MenuItem[] => {
  if (!canViewModule(user, 'settings')) {
    return []
  }

  return settingsMenu
}

export const getDefaultSettingsRoute = (
  user: AuthUser | undefined,
): string | undefined => {
  return getVisibleSettingsMenuItems(user)[0]?.path
}

export const getDefaultAuthorizedRoute = (
  user: AuthUser | undefined,
): string | undefined => {
  if (!user) {
    return undefined
  }

  if (user.role === 'admin') {
    return '/'
  }

  return moduleLandingRoutes.find((route) => canViewModule(user, route.code))
    ?.path
}
